import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

// Two-column row: label + optional description on the left, the
// control on the right.  Replaces the wide-stretched filled field
// pattern with a denser table-like layout where each row is one
// labeled control.  The inner control should suppress its own
// label (e.g. `hiddenLabel` on a TextField) since the row provides
// it externally.
//
// Mobile layout keeps the row direction (label / control side by
// side) and lets the description wrap underneath the label.  This
// reads better than a stacked column where switches end up flung
// to one side and selects fill the whole width awkwardly.
//
// `controlWidth` is the desktop width budget for the right column.
// Default 200 fits short selects ("English", "Imperial",
// "01:00 PM") tightly; bump for longer values (TZ → 380, multi-line
// inputs).  Caps at 50% of the row on narrow viewports so the
// label/description column stays readable.
//
// `stacked` drops the control onto its own full-width row beneath the
// label/description instead of the right column — for controls that
// need the full row to be usable (sliders, multi-line editors).
// `controlWidth` is ignored in this mode.
//
// `divider` draws a 1px bottom border; the last row in a bordered
// container auto-removes its divider via the parent's
// `:last-child` rule.
//
// Negative horizontal margin (paired with matching px) extends the
// row's bottom border to the bordered SettingsSubsection's edges
// so dividers run flush with the container instead of stopping
// short at the subsection's internal padding.  Content stays
// indented because the row reapplies the same px it cancelled out.
export default function SettingsFieldRow({
  label,
  description,
  controlWidth = 200,
  divider = true,
  required = false,
  stacked = false,
  children,
  sx,
}) {
  const { t } = useTranslation();
  return (
    <Stack
      direction={stacked ? 'column' : 'row'}
      spacing={stacked ? 1 : 2}
      alignItems={stacked ? 'stretch' : 'center'}
      sx={{
        py: 1.5,
        px: { xs: 1.5, sm: 2 },
        mx: { xs: -1.5, sm: -2 },
        borderBottom: divider ? 1 : 0,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
        ...sx,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          {required ? (
            <Tooltip title={t('settings.requiredField', 'Required')} arrow>
              <Box
                component="span"
                aria-label={t('settings.requiredField', 'Required')}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          ) : null}
        </Stack>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={
          stacked
            ? {
                // Full-width control row: span the whole row so sliders
                // and the like get a real track instead of collapsing.
                width: '100%',
                '& > .MuiFormControl-root': { width: '100%' },
              }
            : {
                // Cap at controlWidth on large screens but never more
                // than half the row on narrow ones — keeps the
                // label/description column from getting squeezed to
                // one-word-per-line on mobile while letting
                // selects/autocompletes span the desktop ceiling.
                width: `min(${controlWidth}px, 50%)`,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                // Form-control wrappers (TextField, Autocomplete, etc.)
                // get `fullWidth` and need to span the box.  Bare
                // controls (Switch, Button) stay intrinsic-width and sit
                // right-aligned via the parent's `justifyContent`.
                '& > .MuiFormControl-root, & > .MuiAutocomplete-root, & > .MuiTextField-root': {
                  width: '100%',
                },
              }
        }
      >
        {children}
      </Box>
    </Stack>
  );
}
